import { useState } from 'react';
import { api } from '../api.js';
import { getDisplayUnits, formatVolume } from '../units.js';
import { labelStrengthPerMlPerGal, ALK_LABEL_UNITS, strengthPerLitre } from '../custom-dose.js';
import { labelVolumeWord } from '../dose-display.js';

// The keeper's own alkalinity / calcium / magnesium product (feedback
// #60), from the strength printed on its label. The label is the only
// source: we never guess a strength or a daily maximum. The server checks
// the strength against the products we know (a slipped decimal here
// would change every dose tenfold) and answers with the reason.
const NAME = { alk: 'alkalinity', ca: 'calcium', mg: 'magnesium' };
const UNIT = { alk: 'dKH', ca: 'ppm', mg: 'ppm' };

export function CustomDoseProduct({ tank, tankParam, apiParam, onSaved, onCancel }) {
  const stored = tank.params?.[tankParam]?.custom_product || null;
  const [label, setLabel] = useState(stored?.label || '');
  const [ml, setMl] = useState('1');
  const [rise, setRise] = useState(stored ? String(stored.per_ml_per_gal) : '');
  // Alkalinity labels print dKH, meq/L or ppm (as CaCO3); a meq/L figure
  // typed as dKH would make every dose 2.8x too big.
  const [riseUnit, setRiseUnit] = useState('dKH');
  const [vol, setVol] = useState('1');
  // No default: gallons for litres is a 3.8x slip that still passes the
  // server's strength check, so the keeper picks the label's word. A stored
  // product is shown per 1 US gallon, so it starts on gallons.
  const [volUnit, setVolUnit] = useState(stored ? 'gal' : null);
  const [maxMl, setMaxMl] = useState(
    stored?.max_ml_per_gal_day != null ? String(stored.max_ml_per_gal_day) : ''
  );
  // A stored product is shown per 1 gallon. Changing the volume means the
  // keeper is typing the label afresh, so the old numbers are cleared
  // rather than silently reread per the new volume.
  const [prefilled, setPrefilled] = useState(!!stored);
  const changeVolume = (fn) => (e) => {
    fn(e?.target ? e.target.value : e);
    if (prefilled) {
      setRise('');
      setMaxMl('');
      setPrefilled(false);
    }
  };
  const units = getDisplayUnits(tank);
  const tankGal = Number(tank.volume_water_gal) || Number(tank.volume_gallons) || 0;
  const strength = volUnit
    ? labelStrengthPerMlPerGal({
        ml,
        rise,
        riseUnit: apiParam === 'alk' ? riseUnit : 'ppm',
        vol,
        volUnit,
      })
    : null;
  // What those numbers mean, per gallon AND per litre and for this tank, so
  // a slipped decimal or a gallons-for-litres mix-up shows before anything
  // is saved.
  const dp = apiParam === 'alk' ? 2 : 1;
  const readback = strength
    ? `You picked ${volUnit === 'L' ? 'litres' : 'US gallons'}: 1 mL raises ${NAME[apiParam]} ${strength.toFixed(dp)} ${UNIT[apiParam]} in one US gallon, which is ${strengthPerLitre(strength).toFixed(dp)} ${UNIT[apiParam]} in one litre.${
        tankGal > 0
          ? ` So 10 mL in your ${formatVolume(tankGal, units.volume)} of water would raise it about ${((strength * 10) / tankGal).toFixed(dp)} ${UNIT[apiParam]}.`
          : ''
      }`
    : null;
  const [fromLabel, setFromLabel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setErr('');
    if (!volUnit) {
      setErr('Pick gallons or litres, the word the label uses.');
      return;
    }
    if (!strength) {
      setErr('Fill in the three numbers from the label.');
      return;
    }
    const perMlPerGal = strength;
    const volGal = labelStrengthPerMlPerGal.volumeInGallons(vol, volUnit);
    const maxPerGal = maxMl === '' ? null : Number(maxMl) / volGal;
    setBusy(true);
    try {
      await api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: {
          params: {
            [tankParam]: {
              product: 'custom',
              custom_product: {
                label,
                per_ml_per_gal: perMlPerGal,
                max_ml_per_gal_day: maxPerGal,
                from_label: fromLabel,
              },
            },
          },
        },
      });
      await onSaved();
    } catch (e) {
      setErr(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: { params: { [tankParam]: { custom_product: null, product: null } } },
      });
      await onSaved({ removed: true });
    } catch (e) {
      setErr(e.message || 'Could not remove.');
    } finally {
      setBusy(false);
    }
  };

  const num = {
    type: 'number',
    step: 'any',
    inputMode: 'decimal',
    className: 'dose-field',
    style: { width: 80 },
  };
  return (
    <div className="dc-products" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="dc-products-label">Your own {NAME[apiParam]} product</div>
      <label className="ts-field">
        <span>Name</span>
        <input
          type="text"
          value={label}
          maxLength={60}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="As on the bottle"
        />
      </label>
      <div style={{ fontSize: 15, lineHeight: 1.9 }}>
        The label says{' '}
        <input {...num} aria-label="mL of product" value={ml} onChange={changeVolume(setMl)} /> mL
        raises {NAME[apiParam]} by{' '}
        <input
          {...num}
          aria-label={`Rise in ${UNIT[apiParam]}`}
          value={rise}
          onChange={(e) => setRise(e.target.value)}
        />{' '}
        {apiParam === 'alk' ? (
          <select
            className="dose-field"
            value={riseUnit}
            onChange={changeVolume(setRiseUnit)}
            aria-label="Unit on the label"
          >
            {ALK_LABEL_UNITS.map((u) => (
              <option key={u.k} value={u.k}>
                {u.label}
              </option>
            ))}
          </select>
        ) : (
          UNIT[apiParam]
        )}{' '}
        in <input {...num} aria-label="Water volume" value={vol} onChange={changeVolume(setVol)} />{' '}
        of water.
      </div>
      <div role="group" aria-label="Volume unit on the label" style={{ fontSize: 15 }}>
        <div style={{ marginBottom: 6 }}>Does the label say gallons or litres?</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            ['gal', 'US gallons'],
            ['L', 'Litres'],
          ].map(([k, text]) => (
            <button
              key={k}
              type="button"
              className={`dc-product-chip ${volUnit === k ? 'dc-product-chip-on' : ''}`}
              aria-pressed={volUnit === k}
              style={{ flex: 1, minHeight: 44 }}
              onClick={() => k !== volUnit && changeVolume(setVolUnit)(k)}
            >
              <span>{text}</span>
            </button>
          ))}
        </div>
      </div>
      {readback && (
        <p className="dc-hint" style={{ margin: 0 }}>
          {readback} Does that match the label? If not, check the unit and gallons or litres.
        </p>
      )}
      <div style={{ fontSize: 15, lineHeight: 1.9 }}>
        Label&rsquo;s daily maximum, if it gives one (please add it: it is the maker&rsquo;s limit,
        and the calculator holds every dose under it):{' '}
        <input
          {...num}
          aria-label="Daily maximum mL"
          value={maxMl}
          onChange={(e) => setMaxMl(e.target.value)}
        />{' '}
        mL per {vol || '…'} {labelVolumeWord(vol, volUnit)} a day
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 15 }}>
        <input
          type="checkbox"
          checked={fromLabel}
          onChange={(e) => setFromLabel(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span>
          I copied these numbers from the label. If they are off, every dose is off by the same
          amount.
        </span>
      </label>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="ts-save" disabled={busy || !volUnit} onClick={save}>
          {busy ? 'Saving…' : 'Save and use it'}
        </button>
        <button type="button" className="ts-chip" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        {stored && (
          <button type="button" className="ts-chip" disabled={busy} onClick={remove}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
